import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import SalesChannelFaq from '../SalesChannelFaq';
import SalesChannelFeatures from '../SalesChannelFeatures';
import SalesChannelsInstructions from '../SalesChannelsInstructions';

const MAX_ITEMS = 20;

const createTranslationKey = {
	feature: (slug, number) => `${slug}.feature${number}`,
	step: (slug, number) => `${slug}.step${number}`,
	faq: (slug, number) => `${slug}.faq${number}`
};

const useStores = () => {
	const { salesChannel } = useSelector((state) => state.salesChannel);
	const { t } = useTranslation();
	const instructions = useMemo(() => {
		const steps = [];
		for (let stepNumber = 1; stepNumber < MAX_ITEMS; stepNumber += 1) {
			const stepKey = createTranslationKey.step(salesChannel?.slug, stepNumber);
			const hasTranslation = t(stepKey, { returnObjects: true }) !== stepKey;
			if (!hasTranslation) break;
			steps.push(stepKey);
		}

		return steps;
	}, [salesChannel]);

	const features = useMemo(() => {
		const featuresList = [];

		for (let i = 1; i <= MAX_ITEMS; i += 1) {
			const featureKey = createTranslationKey.feature(salesChannel?.slug, i);
			const titleKey = `${featureKey}.title`;
			const hasTitle = t(titleKey, { returnObjects: true }) !== titleKey;
			if (!hasTitle) break;
			featuresList.push({
				key: featureKey,
				title: t(`${featureKey}.title`),
				description: t(`${featureKey}.description`)
			});
		}

		return featuresList;
	}, [salesChannel]);

	const faqs = useMemo(() => {
		const faqsList = [];

		for (let i = 1; i <= MAX_ITEMS; i += 1) {
			const faqKey = createTranslationKey.faq(salesChannel?.slug, i);
			const questionKey = `${faqKey}.question`;
			const hasQuestion = t(questionKey, { returnObjects: true }) !== questionKey;
			if (!hasQuestion) break;
			faqsList.push(faqKey);
		}

		return faqsList;
	}, [salesChannel]);

	const tabs = useMemo(() => {
		const availableTabs = [];

		const tabConfigs = [
			{
				condition: instructions?.length,
				label: 'howToConnect',
				component: () => <SalesChannelsInstructions instructions={instructions} />
			},
			{
				condition: features?.length,
				label: 'availableFeatures',
				component: () => <SalesChannelFeatures features={features} />
			},
			{
				condition: faqs?.length,
				label: 'abbreviation.FAQ',
				component: () => <SalesChannelFaq faqs={faqs} />
			}
		];

		tabConfigs.forEach(({ condition, label, component }) => {
			if (condition) {
				availableTabs.push({ label, component });
			}
		});

		return availableTabs;
	}, [instructions, features, faqs]);

	return { tabs, instructions, features, faqs };
};

export default useStores;
